import { useState, useEffect } from 'react';
import { X, Zap, Clock, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';

// Launch promo configuration - expires Dec 12, 2025 at 1:30pm Greek time (UTC+2)
const PROMO_END_DATE = new Date('2025-12-12T11:30:00.000Z'); // 1:30pm Greek time = 11:30am UTC

// Product Hunt promo configuration - expires Dec 16, 2025 at 11:59pm Greek time (UTC+2)
const PH_PROMO_END_DATE = new Date('2025-12-16T21:59:00.000Z');

// Promo codes for the 50% off launch special
export const LAUNCH_PROMO_CODES = {
  premium_economy: 'LAUNCH50PE',
  first_class: 'LAUNCH50FC',
  stripe_ids: {
    premium_economy: '3I5wON4n',
    first_class: 'wcjT8O0S'
  }
};

// Product Hunt promo code
export const PH_PROMO_CODE = 'ODYVOYPH';

export const isPromoActive = () => {
  return new Date() < PROMO_END_DATE;
};

export const isPHPromoActive = () => {
  return new Date() < PH_PROMO_END_DATE;
};

export const UrgencyBanner = () => {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(true);
  const [timeLeft, setTimeLeft] = useState({ hours: 0, minutes: 0, seconds: 0 });
  const [promoExpired, setPromoExpired] = useState(false);

  // Countdown timer to promo end date
  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date();
      const diff = PROMO_END_DATE.getTime() - now.getTime();
      
      if (diff <= 0) {
        setPromoExpired(true);
        return { hours: 0, minutes: 0, seconds: 0 };
      }
      
      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);
      
      return { hours, minutes, seconds };
    };

    setTimeLeft(calculateTimeLeft());

    const timer = setInterval(() => {
      const newTimeLeft = calculateTimeLeft();
      setTimeLeft(newTimeLeft);
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Check if banner was dismissed today
  useEffect(() => {
    const dismissedDate = localStorage.getItem('urgencyBannerDismissed');
    const today = new Date().toDateString();
    if (dismissedDate === today) {
      setIsVisible(false);
    }
  }, []);

  const handleDismiss = () => {
    setIsVisible(false);
    localStorage.setItem('urgencyBannerDismissed', new Date().toDateString());
  };

  // Don't show if promo expired or dismissed
  if (!isVisible || promoExpired) return null;

  return (
    <div className="bg-gradient-to-r from-primary via-primary/90 to-primary animate-pulse-slow relative overflow-hidden">
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI0MCIgaGVpZ2h0PSI0MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAwIDEwIEwgNDAgMTAgTSAxMCAwIEwgMTAgNDAgTSAwIDIwIEwgNDAgMjAgTSAyMCAwIEwgMjAgNDAgTSAwIDMwIEwgNDAgMzAgTSAzMCAwIEwgMzAgNDAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS1vcGFjaXR5PSIwLjA1IiBzdHJva2Utd2lkdGg9IjEiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZ3JpZCkiLz48L3N2Zz4=')] opacity-30" />
      
      <div className="max-w-7xl mx-auto px-4 py-2.5 sm:py-3 relative">
        <div className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4 text-primary-foreground">
          <div className="flex items-center gap-2 sm:gap-4">
            <div className="flex items-center gap-1.5 sm:gap-2">
              <Zap className="h-4 w-4 animate-pulse" />
              <span className="text-xs sm:text-sm font-semibold">
                🎉 Launch Special
              </span>
            </div>
            
            <div className="hidden sm:flex items-center gap-1.5 bg-white/20 rounded-full px-3 py-1">
              <Clock className="h-3.5 w-3.5" />
              <span className="text-xs font-mono font-bold tabular-nums">
                {String(timeLeft.hours).padStart(2, '0')}:{String(timeLeft.minutes).padStart(2, '0')}:{String(timeLeft.seconds).padStart(2, '0')}
              </span>
            </div>
            
            <span className="text-xs sm:text-sm">
              <span className="font-bold">50% OFF</span>
              <span className="hidden sm:inline"> First Month</span>
            </span>
            
            <Button
              onClick={() => navigate('/pricing?promo=launch50')}
              size="sm"
              variant="secondary"
              className="h-7 px-3 text-xs font-semibold bg-white text-primary hover:bg-white/90"
            >
              Claim Now
            </Button>
          </div>
          
          {/* Promo codes display */}
          <div className="flex items-center gap-2 text-xs">
            <span className="hidden lg:inline opacity-80">Use codes:</span>
            <code className="bg-white/20 px-2 py-0.5 rounded font-mono font-bold">LAUNCH50PE</code>
            <span className="opacity-60">or</span>
            <code className="bg-white/20 px-2 py-0.5 rounded font-mono font-bold">LAUNCH50FC</code>
          </div>
          
          <button
            onClick={handleDismiss}
            className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 p-1 hover:bg-white/20 rounded-full transition-colors"
            aria-label="Dismiss banner"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export const SocialProofBadge = () => {
  const [count, setCount] = useState(0);
  const targetCount = 2847; // Simulate user count

  useEffect(() => {
    // Animate counter on mount
    const duration = 2000;
    const steps = 60;
    const increment = targetCount / steps;
    let current = 0;

    const timer = setInterval(() => {
      current += increment;
      if (current >= targetCount) {
        setCount(targetCount);
        clearInterval(timer);
      } else {
        setCount(Math.floor(current));
      }
    }, duration / steps);

    return () => clearInterval(timer);
  }, []);

  return (
    <div className="flex items-center gap-2 text-muted-foreground">
      <div className="flex -space-x-2">
        {[1, 2, 3, 4].map((i) => (
          <div
            key={i}
            className="w-7 h-7 rounded-full bg-gradient-to-br from-primary/60 to-primary border-2 border-background flex items-center justify-center"
          >
            <Users className="h-3 w-3 text-primary-foreground" />
          </div>
        ))}
      </div>
      <span className="text-sm">
        <span className="font-semibold text-foreground">{count.toLocaleString()}+</span> travelers joined this week
      </span>
    </div>
  );
};

export const LimitedSpotsBadge = () => {
  // Don't show if promo has expired
  if (!isPromoActive()) return null;
  
  return (
    <div className="inline-flex items-center gap-1.5 bg-destructive/10 text-destructive px-3 py-1.5 rounded-full text-sm font-medium animate-pulse">
      <span className="relative flex h-2 w-2">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-destructive opacity-75" />
        <span className="relative inline-flex rounded-full h-2 w-2 bg-destructive" />
      </span>
      Limited spots at launch pricing
    </div>
  );
};
